import {Button, Box, Divider, Stack, Typography} from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {useEffect, useState} from "react";
import {ColDef} from "ag-grid-community";
import {ApiRequestFailed, authenticateAndFetchData, retryFn} from "@lib/apis";
import {useQuery} from "@tanstack/react-query";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import {ArrowBack} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router-dom";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import {useSnackbar} from "notistack";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface Email {
	id: number
	address: string
	username: string
	subdomain: string
}

interface PaginationData {
	user_email: string
	data: Email[]
	total_count: number
	total_pages: number
	current_page: number
	page_size: number
}


export default function AdminUserEmails() {
	const {userId} = useParams();
	const { user } = useAuth();
	const navigate = useNavigate();
	const {enqueueSnackbar} = useSnackbar();

	const [pageData, setPageData] = useState<PaginationData>();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [exportLoading, setExportLoading] = useState(false);

	// Query to fetch all emails.
	const adminUserEmailsQuery = useQuery({
		queryKey: [
			"adminUserEmailsQuery",
			userId,
			page,
			pageSize,
			search
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/get-user-emails/?user_id=${userId}&page=${page}&page_size=${pageSize}&search=${search}`
			),
		gcTime: 0,
		refetchOnWindowFocus: false,
		retry: retryFn,
	});

	useEffect(() => {
		if (adminUserEmailsQuery.data?.data) {
			const response = adminUserEmailsQuery.data.data;

			setPageData({
				user_email: response.user_email,
				data: response.data || [],
				total_count: response.total_count,
				total_pages: response.total_pages,
				current_page: response.current_page,
				page_size: response.page_size,
			});
		}
	}, [adminUserEmailsQuery.data]);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	// Columns for emails table.
	const columnDefs: ColDef[] = [
		{field: "address", headerName: "Email ID"},
		{field: "username", headerName: "Username"},
		{field: "subdomain", headerName: "Subdomain", resizable: false},
	]

	const emailColumns: CSVColumn<any>[] = [
		{ header: "Email Address", accessor: "address" },
		{ header: "Username", accessor: "username" },
		{ header: "Subdomain", accessor: "subdomain" },
	];

	const ExportUserEmailsCsvButton = () => {		

		const handleExportEmails = async () => {
			setExportLoading(true);

			try {
				const response = await authenticateAndFetchData(
					`/admin/get-user-emails/?user_id=${userId}&export_all=true`
				);
				
				const emailsToExport = response?.data?.data || [];

				if (!emailsToExport.length) {
					enqueueSnackbar("No email data available to export.", { variant: "error" });
					return;
				}

				const success = exportToCSV(
					emailsToExport,
					emailColumns,					
					getExportFilename(user?.email ?? "unknown", "Emails")
				);

				if (success) {
					enqueueSnackbar("Emails exported successfully!", { variant: "success" });
				} else {
					enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
				}
			} catch (err) {
				console.error("Export error:", err);
				enqueueSnackbar("Error while exporting emails.", { variant: "error" });
			} finally {
				setExportLoading(false);
			}
		};

		return (
			<Button
				startIcon={<Downloading />}
				variant="contained"
				color="primary"
				onClick={handleExportEmails}
				disabled={exportLoading}
			>
				{exportLoading ? "Exporting..." : "Export"}
			</Button>
		);
	};

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (!pageData && adminUserEmailsQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (adminUserEmailsQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={adminUserEmailsQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
				<Stack direction={"row"} spacing={2} alignItems={"center"}>
					<IconButton onClick={() => navigate(-1)}><ArrowBack/></IconButton>
					<Typography fontWeight={"bold"} variant={"h5"}>Emails ({pageData.user_email})</Typography>
				</Stack>
				<Divider/>
				<Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
					<PaginatedServerSideDataTable
						columns={columnDefs}
						paginationData={pageData}
						loading={adminUserEmailsQuery.isFetching}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						onSearchChange={handleSearchChange}
						searchPlaceholder="Search email, username or subdomain..."
						actions={<ExportUserEmailsCsvButton />}
					/>
				</Box>
			</Stack>
		)
	} else {
		// Ideally it should not reach here.
		return <></>
	}
}
